<script lang="ts">
  import { onMount } from 'svelte';
  import {
    addBlocklistItem,
    blocklistSubject$,
    deleteBlocklistItem,
    pollBlocklistItems,
    type BlocklistItem,
  } from '../../api/blocklist.api';
  import { addToast } from '../../stores/toastStore';

  let items = $state<BlocklistItem[]>([]);
  let mode = $state<'list' | 'add'>('list');

  let newText = $state('');
  let newDescription = $state('');

  const handleDeleteItem = async (item: BlocklistItem) => {
    try {
      await deleteBlocklistItem(item.blocklistItemId);
      blocklistSubject$.next();
      addToast({ message: `Blocked word '${item.text}' removed` });
    } catch (e) {
      addToast({
        message: `Couldn't remove blocked word '${item.text}': ${String(e)}`,
        type: 'error',
      });
    }
  };

  const handleAddItem = async () => {
    if (!newText.trim()) {
      addToast({ message: 'Text cannot be empty', type: 'error' });
      return;
    }
    try {
      await addBlocklistItem({
        text: newText.trim(),
        description: newDescription.trim() || undefined,
      });
      blocklistSubject$.next();
      addToast({ message: `Blocked word '${newText}' added` });
      newText = '';
      newDescription = '';
      mode = 'list';
    } catch (e) {
      addToast({
        message: `Couldn't add blocked word '${newText}': ${String(e)}`,
        type: 'error',
      });
    }
  };

  onMount(() => {
    const subscription = pollBlocklistItems().subscribe({
      next: (data) => (items = data),
      error: (err) => console.error('Blocklist subscription error', err),
    });
    return () => subscription.unsubscribe();
  });
</script>

<div class="container">
  {#if mode === 'add'}
    <form
      class="add-item-container"
      onsubmit={(e) => {
        e.preventDefault();
        handleAddItem();
      }}
      onreset={() => (mode = 'list')}
    >
      <label>
        Word or phrase:
        <input bind:value={newText} placeholder="Enter word or phrase..." />
      </label>
      <label>
        Description (optional):
        <input bind:value={newDescription} placeholder="Brief description..." />
      </label>

      <div class="form-actions">
        <button class="btn" type="submit">Add</button>
        <button class="btn" type="reset">Cancel</button>
      </div>
    </form>
  {:else}
    <div class="header">
      <button class="btn" onclick={() => (mode = 'add')}>+ Add Word</button>
    </div>
    <div class="items">
      {#each items as item (item.blocklistItemId)}
        <div class="item">
          <div class="item-content">
            <strong>{item.text}</strong>
            {#if item.description}
              <span class="description">{item.description}</span>
            {/if}
          </div>
          <button class="btn danger" onclick={() => handleDeleteItem(item)}>
            Remove
          </button>
        </div>
      {/each}
      {#if items.length === 0}
        <p class="empty-state">No banned words yet</p>
      {/if}
    </div>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    font-size: 0.95rem;
  }

  .add-item-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin: 0;
    padding: 0;
    margin-top: 0.25rem;
  }

  .header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }

  .items {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: var(--color-zinc-800);
    border-radius: 0.5rem;
    padding: 0.75rem;
    gap: 0.5rem;
  }

  .item-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 0;
  }

  .item-content strong {
    font-weight: 600;
    word-break: break-word;
  }

  .description {
    color: var(--color-zinc-400);
    font-size: 0.85rem;
  }

  .empty-state {
    text-align: center;
    color: var(--color-zinc-400);
    padding: 1rem;
    margin: 0;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    font-weight: 500;
  }

  input {
    padding: 0.4rem;
    border: 1px solid var(--color-zinc-400);
    border-radius: 0.25rem;
    background-color: var(--color-zinc-200);
    color: var(--color-zinc-950);
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }

  .btn {
    padding: 0.25rem 0.75rem;
    background-color: var(--color-zinc-700);
    border-radius: 0.5rem;
    color: var(--color-zinc-200);
    border: 1px solid var(--color-zinc-600);
    font-size: 1rem;
    cursor: pointer;
  }

  .btn:hover {
    background-color: var(--color-zinc-600);
  }

  .btn.danger {
    background-color: var(--color-red-700);
    border-color: var(--color-red-600);
    padding: 0.25rem 0.6rem;
    font-size: 0.9rem;
  }

  .btn.danger:hover {
    background-color: var(--color-red-600);
  }

  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
</style>
